import type {
  NavigationGuardNext,
  RouteLocationNormalized,
} from 'vue-router'
import { useProjectsStore } from '~stores/projectsStore/useProjectsStore'
import { useAlgorithmsStore } from '~stores/useAlgorithmsStore'
import { RouteNames } from '../../routeNames'


export async function projectTestsNavigationGuard (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const { loadAlgorithmDefinition } = useAlgorithmsStore()
  const { loadProjectByName } = useProjectsStore()

  const project = await loadProjectByName(to.params.projectName as string)
  if (!project) {
    next({ name: RouteNames.NotFound })
    return
  }

  const algorithmDefinition = await loadAlgorithmDefinition({
    id: project.defaultAlgorithmId as string,
    version: project.defaultAlgorithmVersion as string,
  })

  if (!algorithmDefinition?.jobs.annotationPrediction) {
    next({
      name: RouteNames.ProjectDetails,
      params: to.params,
    })
  }

  next()
}

export async function projectAlgorithmGuard (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const {
    isAlgorithmAvailable,
    isAlgorithmInstalled,
    loadAlgorithm,
  } = useAlgorithmsStore()
  const { loadProjectByName } = useProjectsStore()

  const project = await loadProjectByName(to.params.projectName as string)
  const algorithm = await loadAlgorithm(project.defaultAlgorithmId as string, project.defaultAlgorithmVersion as string)
  if (!project) {
    next({ name: RouteNames.NotFound })
    return
  }

  const hasAlgorithm = await isAlgorithmInstalled(algorithm.id, algorithm.version)
  const algoDependentRoutes: RouteNames[] = [
    RouteNames.ProjectTraining,
    RouteNames.ProjectTestSetup,
    RouteNames.ProjectInferenceSetup,
    RouteNames.ProjectRecordingSetup,
    RouteNames.ProjectJobSetup,
  ]

  // Check if algorithm is available but soft-deleted.
  if (
    algoDependentRoutes.includes(to.name as RouteNames) &&
    !isAlgorithmAvailable(algorithm.id, algorithm.version)
  ) {
    next({ name: RouteNames.NotFound })
    return
  }

  // Check if algorithm is unavailable (force-deleted).
  if (!hasAlgorithm) {
    return next({
      hash: to.hash,
      name: RouteNames.NotFoundAlgorithm,
      params: {
        algorithmId: algorithm.id,
        algorithmVersion: algorithm.version,
        pathMatch: decodeURIComponent(to.path).split('/').filter(Boolean),
      },
      query: to.query,
      replace: true,
    })
  }

  next()
}

export async function projectImportDetailsNavigationGuard (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  if (!to.query.importType) {
    console.error('The `importType` query parameter is required.')
  }

  next()
}

export async function projectRecordingsNavigationGuard (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const {
    getAlgorithmDefinition,
    loadAlgorithmDefinition,
  } = useAlgorithmsStore()
  const {
    getProjectByName,
    loadProjectByName,
  } = useProjectsStore()

  const project = getProjectByName(to.params.projectName as string) ??
    await loadProjectByName(to.params.projectName as string)
  if (!project) {
    next({ name: RouteNames.NotFound })
    return
  }

  const algorithmDefinition = getAlgorithmDefinition(
    project.defaultAlgorithmId as string,
    project.defaultAlgorithmVersion as string,
  ) ??
    await loadAlgorithmDefinition({
      id: project.defaultAlgorithmId as string,
      version: project.defaultAlgorithmVersion as string,
    })

  if (!algorithmDefinition?.jobs.recording) {
    next({
      name: RouteNames.ProjectDetails,
      params: to.params,
    })
  }

  next()
}
